// Builds the ordered set of design-system Table column elements for the Opportunities list. Extracted from
// the page so every per-column render callback (name badge + navigation, win-probability dot, currency,
// avatars, the status Pill / contact / date-or-add custom columns, and the kebab actions) is assembled in
// one unit-testable place. The page just drops the returned array into <Table>. Column order mirrors
// OPPORTUNITY_COLUMNS; each column's `visible` is driven by the page's getVisibleColumns result, and each
// sortable column's DS `id` equals its OpportunitySortKey so sort <-> session translation is 1:1.
import React from 'react';
import {Table, ColoredIdIcon} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for the DS Align enum (see the custom columns)
import {Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import {OPPORTUNITY_COLUMNS, type OpportunityColumnName} from './opportunityColumns';
import {opportunitySortingFns, type OpportunityTableRow} from './opportunityTableRows';
import {openAddDates, updateWinProbability, setWinProbability, confirmDeleteOpportunity} from './opportunityRowActions';
import type {StageBand} from './opportunityStage';
import LinkColumn from './columns/LinkColumn';
import StatusPillColumn from './columns/StatusPillColumn';
import QuoteStatusColumn from './columns/QuoteStatusColumn';
import ContactColumn from './columns/ContactColumn';
import DateOrAddColumn from './columns/DateOrAddColumn';
import SalespersonColumn from './columns/SalespersonColumn';
import WinProbabilityColumn from './columns/WinProbabilityColumn';
import TargetValueColumn from './columns/TargetValueColumn';

export interface OpportunityColumnsContext {
	formatMessage: (descriptor: {id: string}) => string;
	// Whether a column is currently visible (from getVisibleColumns: tab / financial permission / eye-selector).
	isVisible: (name: OpportunityColumnName) => boolean;
	// Navigate to a row's project detail (from the name / stage cells).
	onOpenProject: (row: OpportunityTableRow) => void;
	// Navigate to a row's client page (from the Client cell) — a no-op when the row has no client.
	onOpenClient: (row: OpportunityTableRow) => void;
	// Refetch the list after a row mutation (add-dates / delete / update win probability).
	onMutationComplete: () => void;
	/** The account's live stage mapping, for the quick actions' stage-naming toasts. */
	bands: StageBand[];
}

const MESSAGE_ID = OPPORTUNITY_COLUMNS.reduce((acc, c) => {
	acc[c.name] = c.messageId;
	return acc;
}, {} as Record<OpportunityColumnName, string>);

export function buildOpportunityColumns(ctx: OpportunityColumnsContext): React.ReactElement[] {
	const {formatMessage, isVisible, onOpenProject, onOpenClient, onMutationComplete, bands} = ctx;
	const header = (name: OpportunityColumnName): string => formatMessage({id: MESSAGE_ID[name]});
	const dataCy = (name: OpportunityColumnName): string => `opportunities-col-${name}`;

	return [
		<LinkColumn
			key="name"
			id="name"
			accessorKey="name"
			data-cy={dataCy('name')}
			header={header('name')}
			visible={isVisible('name')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.name}
			width="13%"
			icon={(row: OpportunityTableRow) => <ColoredIdIcon color={row.projectColor}>{row.customId}</ColoredIdIcon>}
			href={(row: OpportunityTableRow) => row.projectUrlPath}
			onOpen={onOpenProject}
		/>,
		<LinkColumn
			key="client"
			id="client"
			accessorKey="clientName"
			data-cy={dataCy('client')}
			header={header('client')}
			visible={isVisible('client')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.client}
			width="10%"
			href={(row: OpportunityTableRow) => (row.node.client?.id ? `/clients/${row.node.client.id}` : undefined)}
			onOpen={onOpenClient}
		/>,
		<ContactColumn
			key="primaryContact"
			id="contact"
			accessorKey="contactName"
			data-cy={dataCy('primaryContact')}
			header={header('primaryContact')}
			visible={isVisible('primaryContact')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.contact}
			width="13%"
		/>,
		<TargetValueColumn
			key="targetValue"
			id="targetValue"
			accessorKey="targetValue"
			data-cy={dataCy('targetValue')}
			header={header('targetValue')}
			visible={isVisible('targetValue')}
			rowCurrency={(row: OpportunityTableRow) => row.currency}
			maximumFractionDigits={0}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.targetValue}
			// A blank Target Value (accessor value undefined) sorts to the bottom in both directions, like Win
			// Probability — a plain comparator alone would put blanks on top when ascending.
			sortUndefined="last"
			width="8%"
		/>,
		<WinProbabilityColumn
			key="winProbability"
			id="winChance"
			accessorKey="winChanceSort"
			data-cy={dataCy('winProbability')}
			header={header('winProbability')}
			visible={isVisible('winProbability')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.winChance}
			sortUndefined="last"
			width="8%"
		/>,
		<Table.TextColumn
			key="stage"
			id="stage"
			accessorKey="stageLabel"
			data-cy={dataCy('stage')}
			header={header('stage')}
			visible={isVisible('stage')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.stage}
			ellipsis
			width="10%"
		/>,
		<DateOrAddColumn
			key="startDate"
			id="startDate"
			accessorKey="startDate"
			data-cy={dataCy('startDate')}
			header={header('startDate')}
			visible={isVisible('startDate')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.startDate}
			width="8%"
			dateLabel={(row: OpportunityTableRow) => row.startDateLabel}
			onAddDates={(row: OpportunityTableRow) => openAddDates(row.node, onMutationComplete)}
			missingLabel={formatMessage({id: 'opportunities.start_date_missing'})}
			missingTitle={formatMessage({id: 'opportunities.date_missing.start.title'})}
			missingBody={formatMessage({id: 'opportunities.date_missing.start.body'})}
			missingActionLabel={formatMessage({id: 'opportunities.date_missing.start.action'})}
			closeLabel={formatMessage({id: 'common.close'})}
			missingDataCy="add-start-date"
		/>,
		<DateOrAddColumn
			key="endDate"
			id="endDate"
			accessorKey="endDate"
			data-cy={dataCy('endDate')}
			header={header('endDate')}
			visible={isVisible('endDate')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.endDate}
			width="8%"
			dateLabel={(row: OpportunityTableRow) => row.endDateLabel}
			onAddDates={(row: OpportunityTableRow) => openAddDates(row.node, onMutationComplete)}
			missingLabel={formatMessage({id: 'opportunities.end_date_missing'})}
			missingTitle={formatMessage({id: 'opportunities.date_missing.end.title'})}
			missingBody={formatMessage({id: 'opportunities.date_missing.end.body'})}
			missingActionLabel={formatMessage({id: 'opportunities.date_missing.end.action'})}
			closeLabel={formatMessage({id: 'common.close'})}
			missingDataCy="add-end-date"
		/>,
		<QuoteStatusColumn
			key="quoteStatus"
			id="quoteStatus"
			accessorKey="quoteStatus"
			data-cy={dataCy('quoteStatus')}
			header={header('quoteStatus')}
			visible={isVisible('quoteStatus')}
			align={Align.center}
			publishedLabel={formatMessage({id: 'opportunities.quote_status.published'})}
			approvedLabel={formatMessage({id: 'opportunities.quote_status.approved'})}
			declinedLabel={formatMessage({id: 'opportunities.quote_status.declined'})}
			noQuoteLabel={formatMessage({id: 'opportunities.quote_status.none'})}
			width="6%"
		/>,
		<Table.TextColumn
			key="quoteExpiry"
			id="quoteExpiry"
			accessorKey="quoteExpiryLabel"
			data-cy={dataCy('quoteExpiry')}
			header={header('quoteExpiry')}
			visible={isVisible('quoteExpiry')}
			align={Align.center}
			width="6%"
		/>,
		<StatusPillColumn
			key="status"
			id="status"
			accessorKey="statusState"
			data-cy={dataCy('status')}
			header={header('status')}
			visible={isVisible('status')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.status}
			width="8%"
		/>,
		<SalespersonColumn
			key="salesperson"
			id="salesperson"
			accessorKey="salespersonName"
			data-cy={dataCy('salesperson')}
			header={header('salesperson')}
			visible={isVisible('salesperson')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.salesperson}
			width="10%"
		/>,
		<Table.ActionColumn key="actions" header="" width="48px">
			<Table.ActionColumn.Option
				data-cy="action-update-win-probability"
				onClick={(row: OpportunityTableRow) => updateWinProbability(row.node, onMutationComplete)}
			>
				{formatMessage({id: 'opportunities.action.update_win_probability'})}
			</Table.ActionColumn.Option>
			<Table.ActionColumn.Option
				data-cy="action-set-win-100"
				// Disabled when the row is already at 100% — setting it to 100% would change nothing.
				disabled={(row: OpportunityTableRow) => row.winChance === 1}
				onClick={(row: OpportunityTableRow) => setWinProbability(row.node, 1, onMutationComplete, formatMessage, bands)}
			>
				{formatMessage({id: 'opportunities.action.set_win_probability_100'})}
			</Table.ActionColumn.Option>
			<Table.ActionColumn.Option
				data-cy="action-set-win-0"
				// Disabled when the row is already at 0% — setting it to 0% would change nothing.
				disabled={(row: OpportunityTableRow) => row.winChance === 0}
				onClick={(row: OpportunityTableRow) => setWinProbability(row.node, 0, onMutationComplete, formatMessage, bands)}
			>
				{formatMessage({id: 'opportunities.action.set_win_probability_0'})}
			</Table.ActionColumn.Option>
			<Table.ActionColumn.Option
				data-cy="action-delete"
				onClick={(row: OpportunityTableRow) => confirmDeleteOpportunity(row.node, onMutationComplete, formatMessage)}
			>
				{formatMessage({id: 'common.delete'})}
			</Table.ActionColumn.Option>
		</Table.ActionColumn>,
	];
}
